import { Capacitor } from '@capacitor/core';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { Box, useTheme } from '@mui/material';
import { captureException } from '@sentry/react';
import {
  motion,
  useAnimation,
  useMotionValue,
  ValueAnimationTransition,
} from 'framer-motion';
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { RiveAnimation, RiveAnimationRef } from '@frontend/components';
import { useLoading } from '@frontend/utils';

export interface PullToRefreshProps {
  operationKeys: string[];
}
const PULL_THRESHOLD = 50;
const CONTAINER_HEIGHT = 60;
const TRANSITION_RESET: ValueAnimationTransition<number> = {
  duration: 0.1,
  ease: 'easeOut',
};
const TRANSITION_START: ValueAnimationTransition<number> = {
  type: 'spring',
  stiffness: 300,
  damping: 20,
};

export const PullToRefresh = (props: PropsWithChildren<PullToRefreshProps>) => {
  const isNative = Capacitor.isNativePlatform();
  const riveRef = useRef<RiveAnimationRef>(null);
  const [isRefreshAnimationComplete, setIsRefreshAnimationComplete] =
    useState(false);
  const controls = useAnimation();
  const startYPosition = useRef<number>(0);
  const startXPosition = useRef<number>(0);
  const isPulling = useRef<boolean>(false);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const { operationsInProgress, reload } = useLoading();
  const theme = useTheme();
  const pullDistance = useMotionValue(0);
  const isHapticsTriggered = useRef(false);

  const handleTrigger = useCallback(
    (inputName: string, value?: number | boolean) =>
      riveRef.current?.trigger(inputName, value),
    [],
  );

  // Subscribes once per motion value. It keeps following the motion value while
  // refreshing, so pullDistance.set(0) after a release resets the Rive input.
  useEffect(() => {
    return pullDistance.on('change', async (distance) => {
      handleTrigger('distance', distance * 2);
      if (distance * 2 >= 100 && !isHapticsTriggered.current && isNative) {
        isHapticsTriggered.current = true;
        await Haptics.impact({ style: ImpactStyle.Medium });
      }

      if (distance * 2 < 100 && isHapticsTriggered.current) {
        isHapticsTriggered.current = false;
      }
    });
  }, [pullDistance, handleTrigger, isNative]);

  const resetAnimation = () => {
    handleTrigger('distance', 0);
    handleTrigger('success', false);
    setIsRefreshAnimationComplete(false);
    setIsRefreshing(false);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-checked only when loading or the Rive completion changes; on isRefreshing turning true, reload() has not marked its operations in progress yet, so it would end the refresh at once
  useEffect(() => {
    if (!isRefreshing) {
      return;
    }

    if (!props.operationKeys.some((key) => operationsInProgress.has(key))) {
      if (!isRefreshAnimationComplete) {
        handleTrigger('distance', 0);
        handleTrigger('success', true);
      }

      if (isRefreshAnimationComplete) {
        controls
          .start({ height: 0, transition: TRANSITION_RESET })
          .then(resetAnimation)
          .catch((error) => {
            console.error(error);
            captureException(error);
          });
      }
    }
  }, [operationsInProgress, isRefreshAnimationComplete]);

  const isEventInsideDrawer = (e: React.TouchEvent<HTMLElement>): boolean => {
    const target = e.target as HTMLElement;
    return target.closest('#drawer-bottom') !== null;
  };

  const isEventOnSwiper = (e: React.TouchEvent<HTMLElement>): boolean => {
    const target = e.target as HTMLElement;
    return target.closest('.swiper') !== null;
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLElement>) => {
    if (
      isEventInsideDrawer(e) ||
      e.currentTarget.scrollTop > 0 ||
      isRefreshing
    ) {
      isPulling.current = false;
      return;
    }

    startYPosition.current = e.touches[0]?.clientY ?? 0;
    startXPosition.current = e.touches[0]?.clientX ?? 0;
    isPulling.current = true;
  };

  const handleTouchMove = async (e: React.TouchEvent<HTMLElement>) => {
    if (!isPulling.current || isRefreshing) {
      return;
    }

    const currentY = e.touches[0]?.clientY ?? 0;
    const currentX = e.touches[0]?.clientX ?? 0;
    const distanceY = Math.floor(currentY - startYPosition.current);
    const distanceX = Math.floor(Math.abs(currentX - startXPosition.current));

    if (distanceY > 10) {
      const distanceRatio = distanceY / distanceX; // If it's less than 0.75, assume it's horizontal swiping/pulling.

      // Workaround to avoid wonky behaviour with pull to refresh when swiping cards
      if (isEventOnSwiper(e) && distanceRatio < 0.75) {
        await controls.start({ height: 0, transition: TRANSITION_RESET });
        pullDistance.set(0);
        isPulling.current = false;
        return;
      }
      // Cap the pull distance
      const value = Math.min(distanceY, CONTAINER_HEIGHT);

      if (value === pullDistance.get()) {
        return;
      }

      controls.set({ height: value });
      pullDistance.set(value);
    }
  };

  const handleTouchEnd = async () => {
    if (!isPulling.current) {
      return;
    }

    isPulling.current = false;

    if (isRefreshing || !pullDistance.get()) {
      return;
    }

    if (pullDistance.get() >= PULL_THRESHOLD && !isRefreshing) {
      handleTrigger('release');
      setIsRefreshing(true);
      reload();
      await controls.start({
        height: PULL_THRESHOLD,
        transition: TRANSITION_START,
      });
      pullDistance.set(0);
    } else {
      // Reset back to initial position
      if (pullDistance.get() > 9) {
        handleTrigger('release');
      }
      await controls.start({ height: 0, transition: TRANSITION_RESET });
      resetAnimation();
      pullDistance.set(0);
    }
  };

  if (!isNative) {
    return <>{props.children}</>;
  }

  return (
    <Box
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      sx={{
        overflowY: 'auto',
        height: ['100dvh', '100vh'],
        WebkitOverflowScrolling: 'touch',
        touchAction: 'pan-y',
      }}
    >
      <motion.div
        animate={controls}
        style={{
          backgroundColor: theme.palette.background.paper,
          height: 0,
          maxHeight: CONTAINER_HEIGHT,
        }}
      >
        <Box
          sx={{
            height: PULL_THRESHOLD,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'absolute',
            width: '100%',
          }}
        >
          <RiveAnimation
            onEvent={(e: any) => {
              if (e.data.name === 'complete') {
                setIsRefreshAnimationComplete(true);
              }
            }}
            ref={riveRef}
            src="/rive/pull_to_refresh.riv"
            height={48}
            width={48}
          />
        </Box>
      </motion.div>
      <>{props.children}</>
    </Box>
  );
};
